/**
 * Demographics Legend Configuration
 *
 * Defines legend specifications for all demographics layers
 * including color stops, labels, and units
 */

export interface LegendStop {
  value: number
  color: string
  label: string
}

export interface LegendCategory {
  value: number
  color: string
  label: string
}

export interface GradientLegendConfig {
  type: 'gradient'
  label: string
  unit: string
  stops: LegendStop[]
}

export interface CategoricalLegendConfig {
  type: 'categorical'
  label: string
  categories: LegendCategory[]
}

export type DemographicsLegendConfig =
  | GradientLegendConfig
  | CategoricalLegendConfig

/**
 * Legend configurations for all demographics layers
 */
export const DEMOGRAPHICS_LEGEND_CONFIG: Record<
  string,
  DemographicsLegendConfig
> = {
  'demographics-population-density': {
    type: 'gradient',
    label: 'Population Density',
    unit: 'people/km²',
    stops: [
      { value: 0, color: '#FFFFCC', label: '0' },
      { value: 50, color: '#FFEDA0', label: '50' },
      { value: 100, color: '#FED976', label: '100' },
      { value: 200, color: '#FD8D3C', label: '200' },
      { value: 300, color: '#E31A1C', label: '300' },
      { value: 400, color: '#B10026', label: '400+' },
    ],
  },

  'demographics-foreign-pct': {
    type: 'gradient',
    label: 'Foreign Population',
    unit: '%',
    stops: [
      { value: 0, color: '#EFF3FF', label: '0%' },
      { value: 15, color: '#BDD7E7', label: '15%' },
      { value: 30, color: '#6BAED6', label: '30%' },
      { value: 45, color: '#3182BD', label: '45%' },
      { value: 60, color: '#08519C', label: '60%+' },
    ],
  },

  'demographics-young-adult-pct': {
    type: 'gradient',
    label: 'Young Adults (20-39)',
    unit: '%',
    stops: [
      { value: 0, color: '#EDFDE7', label: '0%' },
      { value: 20, color: '#BAE4B3', label: '20%' },
      { value: 30, color: '#74C476', label: '30%' },
      { value: 40, color: '#31A354', label: '40%' },
      { value: 50, color: '#006D2C', label: '50%+' },
    ],
  },

  'demographics-elderly-pct': {
    type: 'gradient',
    label: 'Elderly (65+)',
    unit: '%',
    stops: [
      { value: 0, color: '#F2F0F7', label: '0%' },
      { value: 15, color: '#CBC9E2', label: '15%' },
      { value: 20, color: '#9E9AC8', label: '20%' },
      { value: 25, color: '#756BB1', label: '25%' },
      { value: 30, color: '#54278F', label: '30%+' },
    ],
  },

  'demographics-growth-rate': {
    type: 'gradient',
    label: 'Population Growth (5-year)',
    unit: '%/year',
    stops: [
      { value: -5, color: '#D7191C', label: '-5%' },
      { value: -2, color: '#FDAE61', label: '-2%' },
      { value: 0, color: '#FFFFBF', label: '0%' },
      { value: 2, color: '#A6D96A', label: '+2%' },
      { value: 5, color: '#1A9641', label: '+5%+' },
    ],
  },

  'demographics-age-group': {
    type: 'categorical',
    label: 'Dominant Age Group',
    categories: [
      { value: 1, color: '#4DAF4A', label: '0-19 years' },
      { value: 2, color: '#377EB8', label: '20-39 years' },
      { value: 3, color: '#E41A1C', label: '40-64 years' },
      { value: 4, color: '#984EA3', label: '65+ years' },
    ],
  },

  'demographics-household-type': {
    type: 'categorical',
    label: 'Dominant Household Type',
    categories: [
      { value: 1, color: '#FF7F00', label: '1 person' },
      { value: 2, color: '#FFFF33', label: '2 persons' },
      { value: 3, color: '#A65628', label: '3 persons' },
      { value: 4, color: '#F781BF', label: '4 persons' },
      { value: 5, color: '#999999', label: '5-6+ persons' },
    ],
  },

  'demographics-residence-duration': {
    type: 'categorical',
    label: 'Dominant Residence Duration',
    categories: [
      { value: 1, color: '#FEE5D9', label: '0-1 year' },
      { value: 2, color: '#FCAE91', label: '1-5 years' },
      { value: 3, color: '#FB6A4A', label: '6-10 years' },
      { value: 4, color: '#CB181D', label: '10+ years' },
    ],
  },
}

/**
 * Get legend configuration for a specific demographics layer
 */
export function getDemographicsLegendConfig(
  layerId: string
): DemographicsLegendConfig | undefined {
  return DEMOGRAPHICS_LEGEND_CONFIG[layerId]
}

/**
 * Check if a layer has a gradient (continuous) legend
 */
export function isGradientLegend(
  config: DemographicsLegendConfig
): config is GradientLegendConfig {
  return config.type === 'gradient'
}

/**
 * Check if a layer has a categorical (discrete) legend
 */
export function isCategoricalLegend(
  config: DemographicsLegendConfig
): config is CategoricalLegendConfig {
  return config.type === 'categorical'
}
